import * as XLSX from 'xlsx/dist/xlsx.full.min';
import { saveAs } from 'file-saver';

// Excel cell character limit is 32,767
const EXCEL_CELL_LIMIT = 32767;
const TRUNCATE_MESSAGE = '... [truncated due to Excel limit]';

const truncateForExcel = (text) => {
	if (text === null || text === undefined) return '';
	
	const stringValue = `${text}`;
	if (stringValue.length <= EXCEL_CELL_LIMIT) {
		return stringValue;
	}
	
	// Truncate and add message
	const availableSpace = EXCEL_CELL_LIMIT - TRUNCATE_MESSAGE.length;
	return stringValue.substring(0, availableSpace) + TRUNCATE_MESSAGE;
};

const parseCSVToArray = (csvString) => {
	const rows = [];
	let currentRow = [];
	let currentField = '';
	let insideQuotes = false;
	
	for (let i = 0; i < csvString.length; i++) {
	  const char = csvString[i];
	  
	  if (char === '"') {
		if (insideQuotes && csvString[i + 1] === '"') {
		  currentField += '"';
		  i++;
		} else {
		  insideQuotes = !insideQuotes;
		}
	  } else if (char === ',' && !insideQuotes) {
		currentRow.push(currentField);
		currentField = '';
	  } else if (char === '\n' && !insideQuotes) {
		currentRow.push(currentField);
		rows.push(currentRow);
		currentRow = [];
		currentField = '';
	  } else {
		currentField += char;
	  }
	}
	
	if (currentField !== '') {
	  currentRow.push(currentField);
	}
	if (currentRow.length > 0) {
	  rows.push(currentRow);
	}
	
	return rows;
}

const DownloadMultiSheetExcel = (filename, sheets) => {
	try {
	  const workbook = XLSX.utils.book_new();
	  
	  sheets.forEach(({name, data}) => {
		// Truncate long text in cells to prevent Excel limit errors
		const truncatedData = data.map(row => 
		  row.map(cell => truncateForExcel(cell))
		);
		
		const worksheet = XLSX.utils.aoa_to_sheet(truncatedData);
		XLSX.utils.book_append_sheet(workbook, worksheet, name);
	  });
	  
	  const excelBuffer = XLSX.write(workbook, {
		bookType: 'xlsx',
		type: 'array'
	  });
	  
	  const blob = new Blob([excelBuffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
	  });
	  saveAs(blob, filename);
	} catch (error) {
	  console.error('Error generating Excel file:', error);
	  throw error;
	}
}

const DownloadXlsx = (fileName,r) =>{
	try{
		const workbook = XLSX.utils.book_new();
		const parsedWorkbook = XLSX.read(r, { type: 'string' });
		const worksheet = parsedWorkbook.Sheets['Sheet1'];
		
		// Process worksheet to truncate long cells
		const range = XLSX.utils.decode_range(worksheet['!ref']);
		for (let row = range.s.r; row <= range.e.r; row++) {
			for (let col = range.s.c; col <= range.e.c; col++) {
				const cellAddress = XLSX.utils.encode_cell({r: row, c: col});
				const cell = worksheet[cellAddress];
				if (cell && cell.v) {
					cell.v = truncateForExcel(cell.v);
					// Update the display text as well if it exists
					if (cell.w) {
						cell.w = truncateForExcel(cell.w);
					}
				}
			}
		}
		
		XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
		XLSX.writeFile(workbook, `${fileName}.xlsx`);
	}catch (error) {
		console.error('Error generating Excel file:', error);
		throw error;
	}
}


const DownloadXlsxFromData = (filename, rows) => {
	try {
		// Create a new workbook
		const workbook = XLSX.utils.book_new();
		
		// Process rows to handle formula injection prevention and Excel cell limits
		const processRow = (row) => {
			return row.map(cell => {
				if (cell === null || cell === undefined) return '';
				
				let innerValue = `${cell}`;
				if (cell instanceof Date) {
					innerValue = cell.toLocaleString();
				}
				
				// Prevent formula injection
				const unsafeFormulaStart = ['=', '+', '@', '\t', '\r'];
				if (innerValue.charAt(0) === '-') {
					if (innerValue !== '-' && isNaN(Number(innerValue))) {
						innerValue = `'${innerValue}`;
					}
				} else if (unsafeFormulaStart.includes(innerValue.charAt(0))) {
					innerValue = `'${innerValue}`;
				}
				
				// Truncate if exceeds Excel cell limit
				return truncateForExcel(innerValue);
			});
		};
		
		// Process all rows
		const processedRows = rows.map(row => processRow(row));
		
		// Create worksheet from array of arrays
		const worksheet = XLSX.utils.aoa_to_sheet(processedRows);
		
		// Add worksheet to workbook
		XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
		
		// Generate Excel file and trigger download
		XLSX.writeFile(workbook, filename);
	} catch (error) {
		console.error('Error generating Excel file:', error);
		throw error;
	}
}

const DownloadCsv = (filename, rows) => {
	var processRow = function (row) {
		var finalVal = '';
		for (var j = 0; j < row.length; j++) {
			var innerValue = row[j] === null ? '' : `${row[j]}`;
			if (row[j] instanceof Date) {
				innerValue = row[j].toLocaleString();
			};
			// Step 1: Prevent formula injection
			const unsafeFormulaStart = ['=', '+', '@', '\t', '\r'];
			// Only quote if starts with '-' and is not a valid number
			if (innerValue.charAt(0) === '-') {
				if (innerValue !== '-' && isNaN(Number(innerValue))) {
					innerValue = `'${innerValue}`;
				}
			} else if (unsafeFormulaStart.includes(innerValue.charAt(0))) {
				innerValue = `'${innerValue}`; // Prepend a single quote
			}
			var result = innerValue.replace(/"/g, '""');
			if (result.search(/("|,|\n)/g) >= 0)
				result = '"' + result + '"';
			if (j > 0)
				finalVal += ',';
			finalVal += result;
		}
		return finalVal + '\n';
	};

	var csvFile = '';
	for (var i = 0; i < rows.length; i++) {
		csvFile += processRow(rows[i]);
	}

	var blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
	if (navigator.msSaveBlob) { // IE 10+
		navigator.msSaveBlob(blob, filename);
	} else {
		var link = document.createElement("a");
		if (link.download !== undefined) { // feature detection
			// Browsers that support HTML5 download attribute
			var url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", filename);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	}
}


const DownloadDirectCsv = (filename, r) => {
	var blob = new Blob([r], { type: 'text/csv;charset=utf-8;' });
	if (navigator.msSaveBlob) { // IE 10+
		navigator.msSaveBlob(blob, filename);
	} else {
		var link = document.createElement("a");
		if (link.download !== undefined) { // feature detection
			// Browsers that support HTML5 download attribute
			var url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute("download", filename);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	}
}

const DownloadText = (filename,r) => {
		const file = new Blob([r], {type: 'text/plain'});
		if (navigator.msSaveBlob) { // IE 10+
			navigator.msSaveBlob(file, filename);
		} else {
			const element = document.createElement("a");
			if(element.download !== undefined) {
				element.href = URL.createObjectURL(file);
				element.download =filename;
				document.body.appendChild(element); // Required for this to work in FireFox
				element.click();
			}
			
		}
		
	  
}

export {
	DownloadCsv, DownloadDirectCsv,DownloadText, DownloadMultiSheetExcel, parseCSVToArray, DownloadXlsx,
	DownloadXlsxFromData
}




